import { useState } from "react";
import {
  FiPackage,
  FiPieChart,
  FiChevronDown,
  FiChevronUp,
  FiAlertTriangle,
  FiInfo,
  FiFileText,
} from "react-icons/fi";

const BundleAnalysis = ({ bundleData }) => {
  const [expandedFile, setExpandedFile] = useState(null);

  // Add debugging
  console.log("BundleAnalysis render - bundleData:", bundleData);

  // Enhanced error handling
  if (!bundleData) {
    return (
      <div className="card">
        <div className="text-center py-8">
          <FiPackage className="h-12 w-12 text-gray-40 mx-auto mb-4" />
          <h4 className="text-heading-02 text-gray-100 font-ibm mb-2">
            Bundle Analysis Unavailable
          </h4>
          <p className="text-body-01 text-gray-70">
            Unable to analyze JavaScript bundles for this application.
          </p>
        </div>
      </div>
    );
  }

  // Add data validation with fallbacks
  const safeData = {
    totalFiles: bundleData.totalFiles || 0,
    totalSize: bundleData.totalSize || 0,
    estimatedGzipSize: bundleData.estimatedGzipSize || 0,
    frameworks: bundleData.frameworks || {},
    files: bundleData.files || [],
    recommendations: bundleData.recommendations || [],
  };

  const formatSize = (bytes) => {
    if (!bytes || bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
  };

  const getFrameworkColor = (framework) => {
    const colors = {
      React: "bg-blue-600",
      Vue: "bg-green-600",
      Angular: "bg-red-600",
      jQuery: "bg-yellow-600",
      Redux: "bg-purple-600",
      Lodash: "bg-orange-500",
      "Moment.js": "bg-red-500",
    };
    return colors[framework] || "bg-gray-60";
  };

  const toggleFile = (index) => {
    setExpandedFile(expandedFile === index ? null : index);
  };

  // Add error boundary wrapper
  try {
    return (
      <div className="space-y-6">
        {/* Bundle Overview */}
        <div className="card">
          <div className="flex items-center space-x-3 mb-6">
            <FiPackage className="h-5 w-5 text-blue-600" />
            <h3 className="text-heading-03 text-gray-100 font-ibm">
              Bundle Analysis
            </h3>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-heading-02 text-gray-100 font-ibm mb-1">
                {safeData.totalFiles}
              </div>
              <div className="text-label-01 text-gray-60 uppercase tracking-wide">
                JS Files
              </div>
            </div>
            <div className="text-center">
              <div className="text-heading-02 text-gray-100 font-ibm mb-1">
                {formatSize(safeData.totalSize)}
              </div>
              <div className="text-label-01 text-gray-60 uppercase tracking-wide">
                Total Size
              </div>
            </div>
            <div className="text-center">
              <div className="text-heading-02 text-gray-100 font-ibm mb-1">
                {formatSize(safeData.estimatedGzipSize)}
              </div>
              <div className="text-label-01 text-gray-60 uppercase tracking-wide">
                Gzipped
              </div>
            </div>
            <div className="text-center">
              <div className="text-heading-02 text-gray-100 font-ibm mb-1">
                {safeData.totalSize > 0
                  ? Math.round(
                      ((safeData.totalSize - safeData.estimatedGzipSize) /
                        safeData.totalSize) *
                        100
                    )
                  : 0}
                %
              </div>
              <div className="text-label-01 text-gray-60 uppercase tracking-wide">
                Compression
              </div>
            </div>
          </div>
        </div>

        {/* Frameworks & Libraries */}
        {Object.keys(safeData.frameworks).length > 0 && (
          <div className="card">
            <div className="flex items-center space-x-3 mb-6">
              <FiPieChart className="h-5 w-5 text-blue-600" />
              <h4 className="text-heading-02 text-gray-100 font-ibm">
                Detected Frameworks & Libraries
              </h4>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(safeData.frameworks).map(([name, data]) => (
                <div key={name} className="flex items-center space-x-3">
                  <div
                    className={`w-3 h-3 rounded-full ${getFrameworkColor(name)}`}
                  ></div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className="text-body-01 text-gray-100 font-medium">
                        {name}
                      </span>
                      <span className="text-body-01 text-gray-70">
                        {formatSize(data.estimatedSize || 0)}
                      </span>
                    </div>
                    <div className="text-label-01 text-gray-60">
                      {data.matches || 0} references
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* File Breakdown */}
        <div className="card">
          <div className="flex items-center space-x-3 mb-6">
            <FiFileText className="h-5 w-5 text-blue-600" />
            <h4 className="text-heading-02 text-gray-100 font-ibm">
              JavaScript Files ({safeData.files.length})
            </h4>
          </div>

          {safeData.files.length === 0 ? (
            <div className="text-center py-8">
              <FiFileText className="h-12 w-12 text-gray-40 mx-auto mb-4" />
              <p className="text-body-01 text-gray-70">
                No JavaScript files detected for analysis.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {[...safeData.files]
                .sort((a, b) => (b.size || 0) - (a.size || 0))
                .slice(0, 10)
                .map((file, index) => (
                  <div
                    key={`file-${index}-${file.url || index}`}
                    className="border border-gray-30 rounded"
                  >
                    <div
                      className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-10"
                      onClick={() => toggleFile(index)}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-body-01 text-gray-100 font-medium truncate">
                          {file.url?.split("/").pop() || "main.js"}
                        </div>
                        <div className="text-label-01 text-gray-60">
                          {formatSize(file.size || 0)} • Gzipped:{" "}
                          {formatSize(file.gzipEstimate || 0)}
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <div className="text-heading-01 text-blue-600 font-ibm">
                          {formatSize(file.size || 0)}
                        </div>
                        {expandedFile === index ? (
                          <FiChevronUp className="h-4 w-4 text-gray-60" />
                        ) : (
                          <FiChevronDown className="h-4 w-4 text-gray-60" />
                        )}
                      </div>
                    </div>

                    {expandedFile === index && (
                      <div className="border-t border-gray-30 p-4 bg-gray-10">
                        <div className="grid sm:grid-cols-2 gap-4">
                          <div>
                            <h5 className="text-heading-01 text-gray-100 font-ibm mb-2">
                              File Details
                            </h5>
                            <div className="space-y-1 text-body-01 text-gray-70">
                              <div>Size: {formatSize(file.size || 0)}</div>
                              <div>
                                Compressed: {formatSize(file.gzipEstimate || 0)}
                              </div>
                              <div>
                                Compression:{" "}
                                {file.size > 0
                                  ? Math.round(
                                      ((file.size - file.gzipEstimate) /
                                        file.size) *
                                        100
                                    )
                                  : 0}
                                %
                              </div>
                              {file.chunks && file.chunks.length > 0 && (
                                <div>Chunks: {file.chunks.length}</div>
                              )}
                            </div>
                          </div>

                          {file.frameworks &&
                            Object.keys(file.frameworks).length > 0 && (
                              <div>
                                <h5 className="text-heading-01 text-gray-100 font-ibm mb-2">
                                  Detected Libraries
                                </h5>
                                <div className="space-y-1">
                                  {Object.entries(file.frameworks).map(
                                    ([name, data]) => (
                                      <div
                                        key={`framework-${name}-${index}`}
                                        className="flex items-center justify-between text-body-01"
                                      >
                                        <span className="text-gray-100">
                                          {name}
                                        </span>
                                        <span className="text-gray-70">
                                          {data.matches || 0} refs
                                        </span>
                                      </div>
                                    )
                                  )}
                                </div>
                              </div>
                            )}
                        </div>

                        <div className="mt-4 pt-4 border-t border-gray-30">
                          <div className="text-label-01 text-gray-60 break-all">
                            {file.url || "Unknown URL"}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
            </div>
          )}

          {safeData.files.length > 10 && (
            <div className="text-center mt-4 pt-4 border-t border-gray-30">
              <span className="text-body-01 text-gray-70">
                ... and {safeData.files.length - 10} more files
              </span>
            </div>
          )}
        </div>

        {/* Bundle Recommendations */}
        {safeData.recommendations.length > 0 && (
          <div className="card">
            <div className="flex items-center space-x-3 mb-6">
              <FiAlertTriangle className="h-5 w-5 text-yellow-600" />
              <h4 className="text-heading-02 text-gray-100 font-ibm">
                Bundle Optimization Recommendations
              </h4>
            </div>

            <div className="space-y-4">
              {safeData.recommendations.map((rec, index) => (
                <div
                  key={`recommendation-${index}`}
                  className="bg-gray-10 p-4 rounded border-l-4 border-yellow-600"
                >
                  <div className="flex items-start space-x-3">
                    <FiInfo className="h-4 w-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h5 className="text-heading-01 text-gray-100 font-ibm mb-1">
                        {rec.title || "Optimization Recommendation"}
                      </h5>
                      <p className="text-body-01 text-gray-70 mb-2">
                        {rec.description || "No description available"}
                      </p>
                      <div className="bg-blue-600 p-3 rounded">
                        <p className="text-body-01 text-white">
                          <strong>Solution:</strong>{" "}
                          {rec.solution || "No solution provided"}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  } catch (error) {
    console.error("BundleAnalysis render error:", error);
    return (
      <div className="card">
        <div className="text-center py-8">
          <FiAlertTriangle className="h-12 w-12 text-red-600 mx-auto mb-4" />
          <h4 className="text-heading-02 text-gray-100 font-ibm mb-2">
            Bundle Analysis Error
          </h4>
          <p className="text-body-01 text-gray-70 mb-4">
            There was an error rendering the bundle analysis.
          </p>
          <p className="text-label-01 text-gray-60">Error: {error.message}</p>
        </div>
      </div>
    );
  }
};

export default BundleAnalysis;
